import { DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { GoogleMapsModule } from '@angular/google-maps';
import { MAT_DIALOG_DATA, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { GOOGLE_MAPS_SETTINGS } from '@core/constants';
import { toRefusal } from '@core/helpers/installation-refusal.helper';
import { LocationModel } from '@core/models/location.model';
import {
  CoordinateResult,
  PreviewAddress,
  SetCoordinates,
} from '@core/models/location-coordinates.model';
import { LocationCoordinatesService } from '@core/services/location-coordinates.service';
import {
  DialogShellComponent,
  FilledButtonComponent,
  SpinnerComponent,
  TextButtonComponent,
} from '@custom-components';
import { DetailFieldComponent } from '@custom-components/general/detail-field';
import { TextFieldComponent } from '@custom-components/general/form/text-field/text-field.component';
import { TableComponent } from '@custom-components/general/table/table.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject, catchError, debounce, map, of, switchMap, tap, timer } from 'rxjs';
import {
  PlacedCoordinateRefusal,
  isResolutionRefusal,
  placeCoordinateRefusal,
  resolutionRefusalKey,
} from '../../coordinate-refusal';

export interface CoordinateMapDialogData {
  locationId: number;
}

// Focus stays on the dialog: the first field the user could type in sits below the map, and focusing
// it would scroll the map out of view.
export function coordinateMapDialogConfig(
  locationId: number,
): MatDialogConfig<CoordinateMapDialogData> {
  return {
    panelClass: 'no-padding-dialog',
    width: '48rem',
    maxWidth: 'calc(100vw - 2rem)',
    autoFocus: 'dialog',
    data: { locationId },
  };
}

export interface Pin {
  lat: number;
  lng: number;
}

// Where the pin first stands: the stored pair, the geocode of the address, or the country.
export type PinStart = 'stored' | 'geocode' | 'country';

export const PREVIEW_DEBOUNCE_MS = 300;
export const REASON_MAX_LENGTH = 255;

const COUNTRY_CENTRES: Record<string, Pin> = {
  NL: { lat: 52.1326, lng: 5.2913 },
  BE: { lat: 50.5039, lng: 4.4699 },
  SG: { lat: 1.3521, lng: 103.8198 },
};
const DEFAULT_COUNTRY = 'NL';

const ADDRESS_ZOOM = 16;
const COUNTRY_ZOOM = 7;

export function countryCentre(country: string | undefined): Pin {
  return COUNTRY_CENTRES[country?.toUpperCase() ?? ''] ?? COUNTRY_CENTRES[DEFAULT_COUNTRY];
}

function pinOf(location: LocationModel): Pin | null {
  const { locationLatitude: lat, locationLongitude: lng } = location;
  if (!lat || !lng) return null;
  const pin = { lat: Number(lat), lng: Number(lng) };
  return Number.isFinite(pin.lat) && Number.isFinite(pin.lng) ? pin : null;
}

function addressOf(location: LocationModel): PreviewAddress | null {
  if (!location.locationPostalCode || !location.locationStreetNumber) return null;
  return {
    postalcode: location.locationPostalCode,
    street_number: location.locationStreetNumber,
    ...(location.locationStreetNumberAddition && {
      street_number_addition: location.locationStreetNumberAddition,
    }),
    ...(location.locationStreet && { street: location.locationStreet }),
    ...(location.locationCity && { city: location.locationCity }),
    ...(location.locationCountry && { country: location.locationCountry }),
  };
}

const INSTALLATION_COLUMNS = [
  { field: 'name', label: 'locations.coordinates.installations.name', width: '28%', bold: true },
  { field: 'current', label: 'locations.coordinates.installations.current', width: '24%' },
  { field: 'resolved', label: 'locations.coordinates.installations.resolved', width: '24%' },
  {
    field: 'reprojection',
    label: 'locations.coordinates.installations.reprojection',
    width: '24%',
  },
];

// Closes with the result of the save, or with nothing when cancelled. `stale` tells the opener
// that the location changed underneath the dialog, so it must read it again.
@Component({
  selector: 'xemex-coordinate-map-dialog',
  imports: [
    DatePipe,
    DetailFieldComponent,
    DialogShellComponent,
    FilledButtonComponent,
    FormsModule,
    GoogleMapsModule,
    SpinnerComponent,
    TableComponent,
    TextButtonComponent,
    TextFieldComponent,
    TranslateModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './coordinate-map-dialog.component.html',
  styleUrl: './coordinate-map-dialog.component.scss',
})
export class CoordinateMapDialogComponent {
  readonly data = inject<CoordinateMapDialogData>(MAT_DIALOG_DATA);
  private readonly dialogRef =
    inject<MatDialogRef<CoordinateMapDialogComponent, CoordinateResult>>(MatDialogRef);
  private readonly service = inject(LocationCoordinatesService);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly installationColumns = INSTALLATION_COLUMNS;
  protected readonly reasonMaxLength = REASON_MAX_LENGTH;

  readonly isLoading = signal(true);
  readonly loadFailed = signal(false);
  readonly location = signal<LocationModel | null>(null);
  readonly pin = signal<Pin | null>(null);
  readonly start = signal<PinStart | null>(null);
  readonly moved = signal(false);
  readonly result = signal<CoordinateResult | null>(null);
  readonly isPreviewing = signal(false);
  readonly previewFailed = signal(false);
  readonly reason = signal('');
  readonly isSaving = signal(false);
  readonly refusal = signal<PlacedCoordinateRefusal | null>(null);
  readonly stale = computed(() => this.refusal()?.stale ?? false);

  // Where the map is centred when it opens. A drag moves the pin, never the map.
  readonly center = signal<Pin>(countryCentre(undefined));
  readonly options = computed<google.maps.MapOptions>(() => ({
    ...GOOGLE_MAPS_SETTINGS,
    zoom: this.start() === 'country' ? COUNTRY_ZOOM : ADDRESS_ZOOM,
    disableDefaultUI: true,
    zoomControl: true,
  }));
  readonly markerOptions = {
    draggable: true,
    icon: { url: '/assets/icons/location_on.svg' },
  };

  private readonly previews = new Subject<{ pin: Pin; delay: number }>();

  // A refusal of the resolution, which the save would meet too, or what a failed save answered.
  readonly messageKey = computed(() => {
    const refused = this.result()?.refusal;
    if (this.refusal()) return this.refusal()!.messageKey;
    return refused ? resolutionRefusalKey(refused) : null;
  });

  readonly installationRows = computed<Record<string, unknown>[]>(() =>
    (this.result()?.installations ?? []).map(change => ({
      name: change.name,
      current: change.currentTimezone ?? '–',
      resolved: change.resolvedTimezone ?? '–',
      reprojection: this.translate.instant(
        change.reprojection
          ? 'locations.coordinates.installations.reprojectionFollows'
          : 'locations.coordinates.installations.noChange',
      ),
    })),
  );

  // Measured when a pin was placed by hand; a geocoded pair has none.
  readonly offsetMetres = computed(() =>
    this.location()?.coordinateSource === 'manual'
      ? (this.location()?.coordinateOffsetM ?? null)
      : null,
  );

  readonly hostsNoInstallation = computed(() => this.result()?.installations.length === 0);
  readonly reasonTooLong = computed(() => this.reason().length > REASON_MAX_LENGTH);

  // A pin that stands where a pair was stored needs no save: the pin must have moved off it, or have
  // been placed on the geocode of a location that had no pair.
  private readonly placed = computed(() => {
    const pin = this.pin();
    const stored = this.location() ? pinOf(this.location()!) : null;
    if (pin === null) return false;
    if (this.start() === 'geocode') return true;
    return this.moved() && (pin.lat !== stored?.lat || pin.lng !== stored?.lng);
  });

  readonly canSave = computed(
    () =>
      this.placed() &&
      this.result()?.refusal === null &&
      this.refusal() === null &&
      !this.isPreviewing() &&
      !this.previewFailed() &&
      !this.isSaving() &&
      !this.reasonTooLong(),
  );

  constructor() {
    this.previews
      .pipe(
        tap(() => {
          this.isPreviewing.set(true);
          this.previewFailed.set(false);
        }),
        debounce(request => timer(request.delay)),
        // The latest drag wins: an answer for an earlier pin is dropped with its request.
        switchMap(({ pin }) =>
          this.service
            .preview(this.data.locationId, { latitude: pin.lat, longitude: pin.lng })
            .pipe(
              map(result => ({ result, failure: null })),
              catchError((error: unknown) => of({ result: null, failure: toRefusal(error) })),
            ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(({ result, failure }) => {
        this.isPreviewing.set(false);
        this.result.set(result);
        if (failure) this.failPreview(failure);
      });
    this.load();
  }

  load(): void {
    this.isLoading.set(true);
    this.loadFailed.set(false);
    this.service
      .location(this.data.locationId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: location => {
          this.location.set(location);
          this.place(location);
        },
        error: (error: unknown) => {
          this.isLoading.set(false);
          const refusal = toRefusal(error);
          if (refusal.status === 404) {
            this.refusal.set(placeCoordinateRefusal(refusal));
          } else {
            this.loadFailed.set(true);
          }
        },
      });
  }

  onDragEnd(event: google.maps.MapMouseEvent): void {
    const position = event.latLng;
    if (!position) return;
    const pin = { lat: position.lat(), lng: position.lng() };
    this.pin.set(pin);
    this.moved.set(true);
    if (!this.stale()) this.refusal.set(null);
    this.previews.next({ pin, delay: PREVIEW_DEBOUNCE_MS });
  }

  save(): void {
    const pin = this.pin();
    if (!pin || !this.canSave()) return;
    const reason = this.reason().trim();
    const body: SetCoordinates = {
      latitude: pin.lat,
      longitude: pin.lng,
      ...(reason && { reason }),
    };
    this.isSaving.set(true);
    this.refusal.set(null);
    this.service
      .set(this.data.locationId, body)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: result => this.dialogRef.close(result),
        error: (error: unknown) => {
          this.isSaving.set(false);
          this.refusal.set(placeCoordinateRefusal(toRefusal(error)));
        },
      });
  }

  cancel(): void {
    this.dialogRef.close();
  }

  // A location with a pair starts on it. One without has no zone to resolve, so the address is
  // geocoded for a starting point, which writes nothing; failing that, the country's centre.
  private place(location: LocationModel): void {
    const stored = pinOf(location);
    if (stored) {
      this.begin(stored, 'stored');
      return;
    }
    const address = addressOf(location);
    if (!address) {
      this.begin(countryCentre(location.locationCountry), 'country');
      return;
    }
    this.service
      .preview(this.data.locationId, { address })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: result => {
          if (result.latitude !== null && result.longitude !== null) {
            this.result.set(result);
            this.begin({ lat: result.latitude, lng: result.longitude }, 'geocode');
          } else {
            this.begin(countryCentre(location.locationCountry), 'country');
          }
        },
        error: () => this.begin(countryCentre(location.locationCountry), 'country'),
      });
  }

  private begin(pin: Pin, from: PinStart): void {
    this.pin.set(pin);
    this.center.set(pin);
    this.start.set(from);
    this.isLoading.set(false);
    if (from !== 'geocode') this.previews.next({ pin, delay: 0 });
  }

  private failPreview(failure: { status: number; reason: string | null }): void {
    if (failure.status === 403 || failure.status === 404 || isResolutionRefusal(failure)) {
      this.refusal.set(placeCoordinateRefusal(failure));
    } else {
      this.previewFailed.set(true);
    }
  }
}
